import React from 'react';
import { useState, useEffect, useMemo, createContext, useContext } from 'react';
import qs from 'qs';
import { DateTime } from 'luxon';
import type { LightningFeature } from 'types';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { metIntegrationV2 } from '@metservicedev/platforms-services-interface/src/services/metIntegration/metIntegration';
import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';

interface queryProps {
    bbox?: [number, number, number, number];
    disabled: boolean;
    providers: string[];
    isLive: boolean;
    timespan?: string; // DateTime--DateTime (2023-04-16T22:00:00Z--2023-04-16T23:00:00Z)
}

const convert360to180 = (lon: number) => {
    if (lon > 180) {
        return lon - 360;
    }
    return lon;
};

export interface LightningContextInterface {
    strikesKey: string;
    totalStrikesReceived: number;
    strikes: {
        type: 'FeatureCollection';
        features: LightningFeature[];
    };
    isLoading: boolean;
    setQuery: (props: queryProps) => void;
}

export const LightningContext = createContext<LightningContextInterface>({
    strikesKey: '',
    totalStrikesReceived: 0,
    strikes: {
        type: 'FeatureCollection',
        features: [],
    },
    isLoading: false,
    setQuery: (props: queryProps) => {},
});

export const useLightningContext = () => useContext(LightningContext);

const genHashKey = (key: string | number, bbox: [number, number, number, number]) => {
    const bboxHash = bbox.join(',');
    return toHash(`${key.toString()}-${bboxHash}`);
};

export const LightningContextProvider = ({ children, metjwt }: { children: React.ReactNode; metjwt?: string }) => {
    const [query, setQuery] = useState<queryProps>({
        bbox: undefined,
        disabled: true,
        providers: ['toa'],
        isLive: false,
        timespan: undefined,
    });
    const { timespan, bbox, isLive, disabled, providers } = query;

    const { stage } = usePlatStageContext();
    const [strikes, setStrikes] = useState<{ [id: string]: LightningFeature }>({});
    const [strikesKey, setStrikesKey] = useState('');
    const [totalStrikesReceived, setTotalStrikesReceived] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [websocketConnection, setWebsocketConnection] = useState<WebSocket>();
    const [websocketNonce, setWebsocketNonce] = useState(0);

    // Lightning cannot query data after now. This validated timespan caps the end date to now and limits the time to 3 hours to work with the lightning api.
    const validatedTimespan = useMemo(() => {
        if (!timespan) return;
        const [start, end] = timespan.split('--');

        if (start > end) return;

        const startTime = DateTime.fromISO(start).toUTC().minus({ hours: 1 }).toISO({ suppressMilliseconds: true });

        return `${startTime}--${end}`;
    }, [timespan]);

    const { lightningApi } = metIntegrationV2;

    const preferenceEvent = useMemo(() => {
        if (!bbox) return;
        if (!isLive) return;

        return JSON.stringify({
            action: 'SET_PREFERENCES',
            bbox,
            providers,
            format: 'application/vnd.metraweather.lightning.geo+json.v3',
        });
    }, [toHash([bbox, providers, isLive])]);

    useEffect(() => {
        if (disabled) return;
        if (!bbox) return;
        if (providers.length === 0) return;
        if (!validatedTimespan) return;

        setIsLoading(true);

        const timespanLength = DateTime.fromISO(validatedTimespan.split('--')[1])
            .diff(DateTime.fromISO(validatedTimespan.split('--')[0]))
            .as('hours');

        // One query per hour. This allows us to exceed the 3h limit from the lightning api
        const queries = Array.from({ length: Math.ceil(timespanLength) }, (_, i) => {
            const start = DateTime.fromISO(validatedTimespan.split('--')[0]).setZone('utc').plus({ hours: i }).toISO({ suppressMilliseconds: true });
            const end = DateTime.min(
                DateTime.fromISO(validatedTimespan.split('--')[0])
                    .setZone('utc')
                    .plus({ hours: i + 1 }),
                DateTime.fromISO(validatedTimespan.split('--')[1]).setZone('utc')
            ).toISO({ suppressMilliseconds: true });

            const [lng1, lat1, lng2, lat2] = bbox;

            return qs.stringify({
                limit: 10_000,
                time: `${start}--${end}`,
                bbox: [convert360to180(lng1), lat1, convert360to180(lng2), lat2].join(','),
                providers: providers.join(','),
            });
        });

        (async () => {
            const params = new URLSearchParams(window.location.search);
            const dashboardId = params.get('id');

            const defaultHeaders = {
                'X-Dashboard-Id': dashboardId || '',
            };
            const headers: { Authorization: string } | {} = metjwt
                ? {
                      ...defaultHeaders,
                      Authorization: `Bearer ${metjwt}`,
                  }
                : defaultHeaders;

            // const responses = await Promise.all(queries.map((query) => fetch(`http://localhost:3000/api/v4/strikes/?${query}`, { headers })));
            const responses = await Promise.all(queries.map((query) => fetch(`https://${lightningApi[stage]}/api/v4/strikes/?${query}`, { headers })));

            const bodies = await Promise.all(responses.map((r) => r.json()));

            if (bodies.some((b) => b.statusCode)) {
                console.warn('there was an error getting lightning');
                return;
            }
            if (bodies.some((b) => b.features.length === 4000)) {
                console.log('The strike limit was reached');
            }
            const features = bodies.map((b) => b.features).flat() as LightningFeature[];

            setStrikes((prev) => {
                // The immutable version of this reduce is O(n^2), but the mutable version is O(n)
                // Usually not a problem, but in this instance there can be as many as 10,000 entities.
                // Performance goes from 18s loading time, to 6ms loading time.
                const newStrikes = features.reduce((acc, strike) => {
                    acc[strike.id] = { ...strike, properties: { ...strike.properties, id: strike.id } };

                    return acc;
                }, prev);

                setStrikesKey(genHashKey(Object.keys(newStrikes).length, bbox));

                return newStrikes;
            });

            setIsLoading(false);
        })();
    }, [toHash(bbox), validatedTimespan, disabled, toHash(providers)]);

    useEffect(() => {
        if (!websocketConnection) return;
        if (!preferenceEvent) return;
        if (websocketConnection.readyState !== 1) return;
        if (disabled) return;

        websocketConnection.send(preferenceEvent);
    }, [preferenceEvent, websocketConnection?.readyState]); // no need to hash preferenceEvent, it's a string

    useEffect(() => {
        if (!isLive) return;
        if (disabled) return;

        console.log({ isLive, websocketNonce, disabled });

        const params = new URLSearchParams(window.location.search);
        const dashboardId = params.get('id');

        if (metjwt) params.append('token', metjwt);
        if (dashboardId) params.append('dashboardId', dashboardId);

        // todo this should check metjwt and conditionally use cloudfront proxy if metjwt is absent
        // const url = `ws://localhost:3000/${params.toString() ? `?${params}` : ''}`;
        const url = `wss://${lightningApi[stage]}/${params.toString() ? `?${params}` : ''}`;
        const ws = new WebSocket(url);

        let totalStrikes = 0;
        ws.addEventListener('message', (event) => {
            const strikeData = JSON.parse(event.data);
            if (strikeData.type !== 'LIGHTNING_STRIKES_RECEIVED') return;
            totalStrikes += strikeData.lightningStrikes.features.length;

            const newStrikes = [...strikeData.lightningStrikes.features].map((strike: LightningFeature) => {
                return {
                    ...strike,
                    properties: {
                        ...strike.properties,
                        id: strike.id,
                    },
                };
            });

            setStrikes((prev) => {
                const updated = { ...prev };
                newStrikes.forEach((strike) => {
                    updated[strike.id] = strike;
                });

                return updated;
            });

            setStrikesKey(genHashKey(totalStrikes, bbox || [0, 0, 0, 0]));
            setTotalStrikesReceived(totalStrikes);
        });

        ws.addEventListener('close', () => {
            console.warn('Lightning websocket closed');
            setTimeout(() => {
                setWebsocketNonce((prev) => prev + 1);
            }, 1000);
        });

        ws.addEventListener('error', (error) => {
            setWebsocketConnection(undefined);
            console.error('Lightning websocket error', error);
            ws.close();
        });

        setWebsocketConnection(ws);

        return () => {
            ws.close();
        };
    }, [isLive, websocketNonce, disabled]);

    useEffect(() => {
        if (disabled) return;
        if (!validatedTimespan) return;

        const strikesToRemove = Object.values(strikes)
            .filter((strike) => {
                const earliestTime = validatedTimespan.split('--')[0];
                if (strike.properties.dateTime < earliestTime) return true;
                return false;
            })
            .map((strike) => strike.id);

        setStrikes((prev) => {
            const newStrikes = strikesToRemove.reduce((acc, id) => {
                delete acc[id];
                return acc;
            }, prev);

            setStrikesKey(genHashKey(Object.keys(newStrikes).length, bbox || [0, 0, 0, 0]));
            return newStrikes;
        });
    }, [validatedTimespan]);

    useEffect(() => {
        if (!bbox) return;
        if (disabled) return;

        const strikesToRemove = Object.values(strikes)
            .filter((strike) => {
                const [lon, lat] = strike.geometry.coordinates;
                if (lon < bbox[0]) return true;
                if (lat < bbox[1]) return true;
                if (lon > bbox[2]) return true;
                if (lat > bbox[3]) return true;
                return false;
            })
            .map((strike) => strike.id);

        setStrikes((prev) => {
            const newStrikes = strikesToRemove.reduce((acc, id) => {
                delete acc[id];
                return acc;
            }, prev);

            setStrikesKey(genHashKey(Object.keys(newStrikes).length, bbox || [0, 0, 0, 0]));
            return newStrikes;
        });
    }, [toHash(bbox)]);

    const geojson = useMemo(() => {
        const features = Object.values(strikes).sort((a, b) => {
            return a.properties.unixTime - b.properties.unixTime;
        });
        return {
            type: 'FeatureCollection' as const,
            features,
        };
    }, [strikesKey]);

    return (
        <LightningContext.Provider value={{ totalStrikesReceived, strikesKey, strikes: geojson, isLoading, setQuery }}>{children}</LightningContext.Provider>
    );
};

export type Strikes = FeatureCollection<Point, LightningFeatureProperties>;
export type Strike = Feature<Point, LightningFeatureProperties>;

export interface LightningFeatureProperties {
    dateTime: string;
    unixTime: number;
    source: 'toa' | 'transpower' | 'mock';
    strike_type: 'GROUND' | 'CLOUD';
    GDOP: number;
    ellipse_bearing: number;
    ellipse_major_axis: number;
    ellipse_minor_axis: number;
    kA?: number;
    id: string;
}
